<template>
  <section>
    <div class="container-fluid">
      <div class="row justify-content-end">

        <div class="col">
          <h4 class="form-head">POD Management</h4>
        </div>
        <div class="col-auto">
          <div class="btn btn-group btn-group">
            <button class="btn btn-dark" v-b-modal.bulkPodUpload title="Bulk Upload POD" v-b-tooltip.hover
                    v-if="isTransporter || isAdmin || isLocalAdmin">
              <i class="float-right fa fa-upload" style="cursor: pointer">&nbsp;</i>
            </button>
            <button class="btn btn-success" v-b-modal.bulkDeliveryStatus title="Bulk Update ADD" v-b-tooltip.hover
                    v-if="isTransporter || isAdmin || isLocalAdmin">
              <i class="float-right fa fa-truck" style="cursor: pointer">&nbsp;</i>
            </button>
            <button class="btn btn-primary" v-b-modal.bulkFileDownload title="Bulk Download POD" v-b-tooltip.hover
                    v-if="!isTransporter">
              <i class="float-right fa fa-download" style="cursor: pointer">&nbsp;</i>
            </button>
          </div>
        </div>
      </div>
      <b-card style="background-color: #F9F9F9;">
        <form>
          <div class="form-row">
            <!-- from date -->
            <div class="form-group col-2">
              <label>From Date</label>
              <date-picker v-model="s.fromDate" :config="options"></date-picker>
            </div>
            <!-- to date -->

            <div class="form-group col-2">
              <label>To Date</label>
              <date-picker v-model="s.toDate" :config="options"></date-picker>
            </div>

            <!-- warehouse-->
            <div class="form-group col-2">
              <label>Warehouse</label>
              <v-select v-model="s.location" :options="locations" label="name" multiple>
                <template slot="option" slot-scope="option">
                  {{ option.name }} &ndash; {{ option.codes.join(',') }}
                </template>
              </v-select>
            </div>
            <!-- transporter -->

            <div class="form-group col-2" v-if="!isTransporter">
              <label>Transporter</label>
              <v-select v-model="s.transporter" :options="transporters" label="name" multiple>
                <template slot="option" slot-scope="option">
                  {{ option.name.substring(0, 10) }}
                </template>
              </v-select>
            </div>
            <!-- customer -->
            <div class="form-group col-3" v-if="!isTransporter && !isCustomer">
              <label>Customer</label>
              <v-select v-model="s.customer" :options="customers" label="name" multiple>
                <template slot="option" slot-scope="option">
                  {{ option.code }} - {{ option.name }}
                </template>
              </v-select>
            </div>

            <div class="form-group col-1">
              <label>&nbsp;</label> <br/>
              <button class="btn btn-primary btn-sm" type="button" @click="getPOD" v-if="!podSearching">
                <i class="fa fa-search"></i> Search
              </button>
              <button class="btn btn-primary btn-sm" type="button" @click="getPOD" v-if="podSearching">
                <i class="fa fa-search fa-spin"></i> Searching
              </button>
            </div>
          </div>
        </form>
      </b-card>
      <b-modal id="filterEwbs" title="Filter Results" ref="filterEwbs" size="lg">
        <!-- filters -->
        <div class="form-row">

          <div class="form-group col-2">
            <label>Divisions</label>
            <v-select v-model="f.divisions" :options="divisions"
                      placeholder="Division"
                      :searchable="false"
                      label="text" multiple>
              <template slot="option" slot-scope="option">
                {{ option.text }}
              </template>
            </v-select>
          </div>
          <div class="col-2">
            <b-form-group label="Invoice No">
              <b-form-input type="text" v-model="f.invoiceNo"
                            placeholder="Invoice No"/>
            </b-form-group>
          </div>
          <div class="col-1">
            <b-form-group label="LR No">
              <b-form-input type="text" v-model="f.lrNo" placeholder="LR No"/>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="Cancelled?">
              <b-form-select v-model="f.cancelled" :options="cancelledOptions"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="NPD?">
              <b-form-select v-model="f.npdShipment" :options="npdOptions"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="Sale Types">
              <b-form-tags v-model="f.saleTypes" add-on-change no-outer-focus class="mb-2">
                <template v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }">
                  <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2">
                    <li v-for="tag in tags" :key="tag" class="list-inline-item">
                      <b-form-tag
                        @remove="removeTag(tag)"
                        :title="tag"
                        :disabled="disabled"
                        variant="info"
                      >{{ tag }}
                      </b-form-tag>
                    </li>
                  </ul>
                  <b-form-select
                    v-bind="inputAttrs"
                    v-on="inputHandlers"
                    :disabled="disabled || availableOptions.length === 0"
                    :options="availableOptions">
                    <template v-slot:first>
                      <!-- This is required to prevent bugs with Safari -->
                      <option disabled value="">Choose a Sales Type...</option>
                    </template>
                  </b-form-select>
                </template>
              </b-form-tags>
            </b-form-group>
          </div>
        </div>
        <div class="form-row">
          <div class="col-auto">
            <b-form-group label="Delivery Status">
              <b-form-select :options="deliveredOptions" v-model="f.delivered"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="Shipment Status">
              <b-form-select :options="shipmentOptions" v-model="f.shipped"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="POD Health">
              <b-form-select v-model="f.goodPOD" :options="goodPODOptions"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="POD Status">
              <b-form-select :options="noPODOptions" v-model="f.noPOD"></b-form-select>
            </b-form-group>
          </div>
          <div class="col-auto">
            <b-form-group label="POD Verified Status?">
              <b-form-select v-model="f.verified" :options="podVerifiedStatus"></b-form-select>
            </b-form-group>

          </div>
          <div class="col">&nbsp;</div>
          <!-- placeholder to use space-->
          <div class="col text-right" style="margin-top: 20px">

          </div>
        </div>
        <template slot="modal-footer">
          <b-button type="button" @click="filterPOD()" variant="info">
            <i class="fa fa-filter"></i> Filter Results
          </b-button>
          <b-button type="button" @click="clear()" variant="warning">
            <i class="fa fa-times-circle"></i> Clear
          </b-button>
        </template>
      </b-modal>
      <!-- view -->
      <div class="row mt-3 mb-3 justify-content-between">
        <div class="col-auto mr-5">
          <h4 class="form-head">Results</h4>
        </div>
        <div class="col">
          <ul class="list-group list-group-horizontal" v-if="makeFilters.length > 0">
            <li class="list-group-item list-group-item-info pr-3" style="padding: 5px;">Filters</li>
            <li class="list-group-item text-center" style="padding: 5px;" v-for="f in makeFilters" :key="f.what">
              <span class="pr-1">{{ f.what }}</span>
              <span v-if="f.val === 'Yes'" class="text-success"><i class="fa fa-check"></i></span>
              <span v-if="f.val === 'No'" class="text-danger"><i class="fa fa-times"></i></span>
              <span v-if="f.val !== 'No' && f.val !== 'Yes'" class="text-info">{{ f.val }}</span>
            </li>
          </ul>
        </div>
        <div class="col-auto">
          <h6 class="form-head mt-1">Total Results &ndash; {{ totalItems }}</h6>
        </div>
        <div class="col-auto">

          <form method="post" :action="csvDownloadUrl" target="_blank">
            <b-button-group>
              <input type="hidden" name="fromDate" :value="sFromDate"/>
              <input type="hidden" name="toDate" :value="sToDate"/>
              <input type="hidden" name="locations" :value="sLocations()"/>
              <input type="hidden" name="transporters" :value="sTransporters()"/>
              <input type="hidden" name="customers" :value="sCustomers()"/>
              <input type="hidden" name="filter" :value="sFilter()"/>
              <b-button type="submit" variant="secondary" size="sm">
                <i class="fa fa-download"></i> Excel
              </b-button>
              <b-button size="sm" @click="showFilter()" variant="primary">
                <i class="fa fa-filter"></i> Filter
              </b-button>
            </b-button-group>
          </form>
        </div>
      </div>
      <div class="row">
        <div class="col">

          <table class="table table-sm table-responsive-sm table-bordered text-center fixed_headers">
            <thead class="thead-light one">
            <tr>
              <th>&nbsp;</th>
              <th>&nbsp;</th>
              <th colspan="2">Invoice</th>
              <th colspan="1">Shipped</th>
              <th colspan="2" v-if="!isCustomer">Customer</th>
              <th colspan="2">Forward</th>
              <th colspan="1">Actual</th>
              <th colspan="1">Estimated</th>
              <th colspan="3">Return</th>
              <th>Number</th>
              <th>Actual Date</th>
              <th colspan="1" v-if="!isTransporter">Transporter</th>
              <th colspan="2" v-if="!isCustomer">&nbsp;</th>
              <th colspan="1" v-if="isCustomer">&nbsp;</th>
              <th colspan="1">Actions</th>
            </tr>
            </thead>
            <thead class="thead-light two">
            <tr>
              <th>DIV</th>
              <th>Warehouse</th>
              <th>
                <a @click="sortPOD('docNo')">
                  No
                  <i class="fa fa-angle-up" v-if="sortBy === 'docNo' && sort === 'asc'"></i>
                  <i class="fa fa-angle-down" v-if="sortBy === 'docNo' && sort === 'desc'"></i>
                </a>
              </th>
              <th>
                <a @click="sortPOD('docDt')">
                  Date
                  <i class="fa fa-angle-up" v-if="sortBy === 'docDt' && sort === 'asc'"></i>
                  <i class="fa fa-angle-down" v-if="sortBy === 'docDt' && sort === 'desc'"></i>

                </a>
              </th>
              <th>
                <a @click="sortPOD('shippedValue')">
                  Value
                  <i class="fa fa-angle-up" v-if="sortBy === 'shippedValue' && sort === 'asc'"></i>
                  <i class="fa fa-angle-down" v-if="sortBy === 'shippedValue' && sort === 'desc'"></i>
                </a>
              </th>
              <th v-if="!isCustomer">Code</th>
              <th v-if="!isCustomer">Name</th>
              <th>LR</th>
              <th>Date</th>
              <th>Ship Date</th>
              <th>Delivery Date</th>
              <th>LR</th>
              <th>CARE</th>
              <th>Date</th>
              <th>Of Pkg</th>
              <th>Of Delivery</th>
              <th v-if="!isTransporter">Code</th>
              <th>Status</th>
              <th v-if="!isCustomer">Comments</th>
              <th>
                <form method="post" :action="imageDownloadUrl" target="_blank">
                  <input type="hidden" name="id" :value="id" v-for="id of downloadQueue" :key="id"/>
                  <button type="submit"
                          v-b-tooltip.hover
                          title="Download All as ZIP"
                          @click="downloadBulk()"
                          class="btn btn-sm btn-primary" v-if="downloadQueue.length > 0">
                    Download <span class="badge badge-light">{{ downloadQueue.length }}</span>
                    <span class="sr-only">unread messages</span>
                  </button>
                </form>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="p in filteredPod" :key="p.id"
                :class="{'text-muted' : p.returnLrNo || p.cancelled, 'text-secondary' : p.goodPod !== null }">
              <td>{{ p.newDivisionD }}</td>
              <td>{{ p.locationCode }}</td>
              <td>{{ p.docNo }}</td>
              <td><span class="one-line-text">{{ p.docDt | date }}</span></td>
              <td>{{ p.shippedValue | toNumber }}</td>
              <td v-if="!isCustomer">
                {{ p.customerCode }}
              </td>
              <td v-if="!isCustomer">
                <div class="one-line-text hide-excess text-left">
                  {{ p.customerName }}
                </div>
              </td>
              <td>
                <div v-if="p.lrNo">
                  <a v-if="p.transporter && p.transporter.trackingUrl"
                     :href="p.transporter.trackingUrl.replace('%docket%',p.lrNo)" target="_blank">
                    {{ p.lrNo }}
                  </a>
                  <span v-if="p.transporter && !p.transporter.trackingUrl">
                    {{ p.lrNo }}
                  </span>
                </div>
              </td>
              <td><span class="one-line-text">{{ p.lrDt|date }}</span></td>
              <td>{{ p.actualShipDate|date }}</td>
              <td>
                 <span class="text-info font-italic"
                       style="font-size: xx-small"
                       title="Estimated Delivery Date">
                  {{ p.estimatedADD|date }}
                </span>
              </td>
              <td>
                <div v-if="p.returnLrNo">
                  <a v-if="p.transporter && p.transporter.trackingUrl"
                     :href="p.transporter.trackingUrl.replace('%docket%',p.lrNo)" target="_blank">
                    {{ p.returnLrNo }}
                  </a>
                  <span v-if="p.transporter && !p.transporter.trackingUrl">
                    {{ p.returnLrNo }}
                  </span>
                </div>
              </td>
              <td>{{ p.grn }}</td>
              <td>
                <span class="one-line-text">
                  <span class="text-danger"
                      v-if="p.deliveryStatus === 'RTO' || (p.returnLrNo !== null && p.returnLrNo.trim().length > 0)|| (p.grn !== null && p.grn.trim().length > 0)">
                    <i class="fa fa-undo"></i>  {{ p.actualDeliveryDate|date }}
                  </span>
                </span>
              </td>
              <td>{{ p.noOfPkg }}</td>
              <td>
                  <span class="text-success"
                        style="font-size: xx-small"
                        title="Actual Delivery Date"
                        v-if="p.deliveryStatus === 'DELIVERED' && (p.returnLrNo === null || p.returnLrNo.trim().length === 0) && (p.grn === null || p.grn.trim().length === 0)">
                  <i class="fa fa-check"></i> {{ p.actualDeliveryDate|date }}
                </span>
              </td>
              <td v-if="!isTransporter">
                {{ p.transporterCode }}
              </td>
              <td class="text-center">
                <p-o-d-status v-bind:docket="p" :key="p.containsPOD"></p-o-d-status>
              </td>
              <td v-if="!isCustomer">
                <ul class="list-unstyled">
                  <li v-for="c in p.comments" :key="c">{{ c }}</li>
                </ul>
              </td>

              <td>
                <div class="btn-group" v-if="p.cancelled === false">
                  <b-btn v-b-tooltip.hover
                         title="Upload POD"
                         variant="primary"
                         size="xs"
                         v-b-modal.podUpload
                         v-if="(isTransporter || isAdmin || isLocalAdmin) && p.containsPOD !== true"
                         @click="uploadPOD(p)">
                    <i class="fa fa-upload"></i>
                  </b-btn>
                  <b-btn v-b-tooltip.hover
                         title="View POD"
                         variant="info"
                         size="xs"
                         v-if="p.containsPOD === true" @click="showPOD(p)">
                    <i class="fa fa-eye"></i>
                  </b-btn>
                  <b-btn v-b-tooltip.hover
                         title="Add/View Comments"
                         variant="secondary"
                         size="xs"
                         v-if="!isCustomer"
                         v-b-modal.commentView
                         @click="addComments(p)">
                    <i class="fa fa-comments"></i>
                  </b-btn>
                  <b-btn v-b-tooltip.hover
                         v-if="p.containsPOD === true && downloadQueue.indexOf(p.id) === -1"
                         title="Add To Download Queue"
                         variant="success"
                         size="xs"
                         @click="addToDownloadQueue(p)">
                    <i class="fa fa-download"></i>
                  </b-btn>

                  <b-btn v-b-tooltip.hover
                         v-if="p.containsPOD === true && downloadQueue.indexOf(p.id) > -1"
                         title="Remove From Download Queue"
                         variant="warning"
                         size="xs"
                         @click="removeFromDownloadQueue(p)">
                    <i class="fa fa-times"></i>
                  </b-btn>
                  <b-btn v-b-tooltip.hover
                         v-if="(isAdmin || isLocalAdmin) && !isCustomer && !isTransporter"
                         title="Delete Uploaded PODs/ADD"
                         variant="danger"
                         size="xs"
                         v-b-modal.podDelete
                         @click="podDelete(p)">
                    <i class="fa fa-times"></i>
                  </b-btn>

                </div>
              </td>
            </tr>
            </tbody>
          </table>
          <b-pagination size="md"
                        align="center"
                        :limit="20"
                        @change="changePage"
                        :total-rows="totalItems" v-model="currentPage" :per-page="perPage">
          </b-pagination>

        </div>
      </div>
    </div>
    <b-modal id="podView" ref="podView" title="View POD" size="lg" centered lazy hide-footer>
      <basic-docket v-bind:docket="selectedDocket" v-bind:comments="podComments"></basic-docket>
      <h4 class="form-head">Uploaded Files <span class="badge badge-primary">{{ podFiles.length }}</span></h4>
      <div class="row justify-content-end" v-for="pi of podFiles" :key="pi.id">
        <div class="col-12">
          <div class="pod_image" id="pod_image">
            <iframe :src="viewPodLink(pi, false)" class="m-3" :height="iframeHeight" :width="iframeWidth"
                    style="box-shadow: 2px 3px 4px #aaa;">
              <loading></loading>
            </iframe>
          </div>
        </div>
        <div class="col-auto mb-3" style="align-self: flex-end">
          <a class="btn btn-sm btn-primary" :href="viewPodLink(pi, true)" target="_blank">
            <i class="fas fa-download"></i> Download
          </a>
        </div>
      </div>
      <div class="row justify-content-start mt-5" v-if="!isCustomer">
        <div class="col-sm-12">
          <b-form>
            <b-form-group label="Add a Comment">
              <b-form-textarea id="textarea2x"
                               v-model="newComment"
                               placeholder="Add a Comment"
                               :rows="3"
                               :max-rows="6">
              </b-form-textarea>
            </b-form-group>
          </b-form>
        </div>
      </div>
      <div class="row justify-content-end mt-5">
        <div class="col-auto">

          <b-btn size="" class="" variant="info" v-if="!isCustomer" @click="saveComment">
            <i class="fa fa-save"></i> Save Comment
          </b-btn>
          <b-btn size="" class="" variant="primary" v-if="!isCustomer" @click="verifiedPOD">
            <i class="fas fa-check"></i> Good POD
          </b-btn>
          <b-btn size="" class="" variant="danger" v-if="!isCustomer" @click="makeBadPOD">
            <i class="fas fa-times-hexagon"></i> Bad POD
          </b-btn>
          <b-btn size="" class="" variant="warning" @click="hideModal">
            <i class="fas fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <b-modal id="podUpload" ref="podUpload" title="Upload POD" size="lg" centered lazy hide-footer>
      <basic-docket v-bind:docket="selectedDocket" v-bind:comments="podComments"></basic-docket>
      <h5 class="form-head">POD Upload</h5>
      <b-list-group>
        <b-list-group-item v-for="c of podFiles" :key="c.id">
          <div class="d-flex w-100 justify-content-between">
            <h5 class="mb-1"><span v-if="c.uploadedBy">{{ c.uploadedBy.userName }}</span></h5>
            <small>{{ c.uploadedAt|datetime }}</small>
          </div>
          <p class="mb-1">
            <a href="#">{{ c.fileName }}</a>
          </p>
          <small class="text-muted">{{ c.comment }}</small>
        </b-list-group-item>
      </b-list-group>

      <div class="row">
        <div class="col">
          <b-form>

            <b-form-group label="New File">
              <b-form-file v-model="podFile" accept="image/*, application/pdf" :state="true"
                           placeholder="Choose a file..."
                           @change="onFileChange"></b-form-file>
            </b-form-group>

            <b-form-group label="Good POD?">
              <b-form-radio-group id="radios2" v-model="goodPod" name="radioSubComponent">
                <b-form-radio value="true">Good POD</b-form-radio>
                <b-form-radio value="false">BAD POD</b-form-radio>
              </b-form-radio-group>
            </b-form-group>

            <b-form-group label="Add a Comment">
              <b-form-input id="textarea2"
                            v-model="newComment"
                            placeholder="Enter a Comment"
                            :required="goodPod === false">
              </b-form-input>
            </b-form-group>

            <div class="form-group">
              <label>Shipment Status</label>
              <select class="form-control" v-model="deliveryStatus">
                <option value="DELIVERED">Delivered</option>
                <option value="RTO">Returned</option>
              </select>
            </div>
            <div class="form-group">
              <label>Delivery Date</label>
              <date-picker v-model="add" :config="options"></date-picker>
            </div>

          </b-form>
        </div>
        <div class="col" v-if="previewType === 'image' && previewUrl">
          <h5 class="form-head">Preview</h5>
          <img v-if="previewUrl && previewType === 'image'" :src="previewUrl"
               style="max-height: 300px;max-width: 300px;"/>
          <canvas v-if="previewUrl && previewType === 'pdf'" id="the-canvas"></canvas>
        </div>
      </div>
      <div class="row justify-content-end mt-5">
        <div class="col-auto">
          <b-btn size="" class="" variant="primary" @click="savePOD">
            <i class="fa fa-save"></i> Save POD
          </b-btn>

          <b-btn size="" class="" variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <b-modal id="podDelete" ref="podDelete" title="Delete POD/ADD or Both" size="lg" centered lazy hide-footer>
      <basic-docket v-bind:docket="selectedDocket" v-bind:comments="podComments"></basic-docket>
      <h5 class="form-head">Delete POD/ADD</h5>
      <b-form-group size="md" label="What to Delete?">
        <b-form-radio :disabled="selectedDocket && selectedDocket.containsPOD !== true" v-model="deleteSelected"
                      value="pod">Delete POD
        </b-form-radio>
        <b-form-radio v-model="deleteSelected" value="add">Delete ADD</b-form-radio>
        <b-form-radio :disabled="selectedDocket && selectedDocket.containsPOD !== true" v-model="deleteSelected"
                      value="both">Delete POD and ADD
        </b-form-radio>
      </b-form-group>

      <div class="row justify-content-end mt-5">
        <div class="col-auto">
          <b-btn size="" class="" variant="primary" @click="deletePOD">
            <i class="fa fa-save"></i> Delete
          </b-btn>

          <b-btn size="" class="" variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <b-modal id="bulkPodUpload" ref="bulkPodUpload" title="Upload POD" size="lg" centered lazy hide-footer>
      <h4 class="form-head">Bulk POD Upload</h4>

      <b-form>

        <b-form-group label="Select PODs">
          <b-form-file v-model="podFiles" plain multiple
                       @change="filesSelected()"
                       ref="podFilesInput"
                       accept="image/jpeg, image/png, image/gif, application/pdf"
                       placeholder="Choose files ..."></b-form-file>

        </b-form-group>

        <table class="table mt-3 table-sm" id="bulkPodTable" style="font-size: medium">
          <thead class="thead-dark">
          <tr>
            <th>File</th>
            <th>Document Type</th>
            <th>LR/Invoice No</th>
            <th>Good POD?</th>
            <th>Delivery Date (YYYY-MM-DD)</th>
            <th>Shipment Status</th>
            <th>Comment</th>
            <th>&nbsp;</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="f in bulkPods" :key="f.idx">
            <td>{{ f.name }}</td>
            <td>
              <b-form-select v-model="f.docType" :options="docTypeOptions" @change="fetchADD(f)"
                             @blur="fetchADD(f)"></b-form-select>
            </td>
            <td>
              <b-form-input v-model="f.lrNo" type="text" @change="fetchADD(f)" @blur="fetchADD(f)"></b-form-input>
            </td>
            <td>
              <b-form-radio-group v-model="f.goodPod" :required="false">
                <b-form-radio value="true">Good POD</b-form-radio>
                <b-form-radio value="false">BAD POD</b-form-radio>
              </b-form-radio-group>
            </td>
            <td>
              <input type="text" v-model="f.add"
                     @click="setDatePickerVisible(f.idx)"
                     @focus="setDatePickerVisible(f.idx)"
                     class="form-control" readonly/>
              <div class="dp-holder" v-if="showDatePicker(f.idx)">
                <date-picker v-model="f.add" :config="inlineOptions"
                             @dp-change="setDatePickerVisible(-1)"
                             @change="setDatePickerVisible(-1)"></date-picker>
              </div>
            </td>
            <td>
              <select class="form-control" v-model="f.deliveryStatus">
                <option value="DELIVERED">Delivered</option>
                <option value="RTO">Returned</option>
              </select>
            </td>
            <td>
              <b-form-input v-model="f.comment"
                            placeholder="Enter a Comment"
                            :required="f.goodPod === false">
              </b-form-input>
            </td>
            <td>
                <span :class="'text-'+f.status">
                  <i class="fa fa-times-circle" v-if="f.status === 'danger'"></i>
                  <i class="fa fa-exclamation" v-if="f.status === 'info'"></i>
                  <i class="fa fa-check" v-if="f.status === 'success'"></i>
                  <i class="fa fa-exclamation-circle" v-if="f.status === 'warning'"></i>
                  &nbsp;{{ f.errMsg }}
                </span>
            </td>
          </tr>
          </tbody>
        </table>

      </b-form>
      <div class="row justify-content-center m-5" v-if="savingBulkPOD">
        <loading></loading>
      </div>
      <div class="row justify-content-end mt-5" v-if="!savingBulkPOD">
        <div class="col-auto">
          <b-btn size="" class="" variant="primary" @click="validatePods"
                 v-if="sizeValidated == true && !bulkValidated && bulkPods.length > 0">
            <i class="fa fa-save"></i> Validate PODs
          </b-btn>

          <b-btn size="" class="" variant="success" @click="savePODs" v-if="bulkValidated">
            <i class="fa fa-save"></i> Save PODs
          </b-btn>

          <b-btn size="" class="" variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <b-modal id="commentView" ref="commentView" title="Comments" size="lg" centered lazy hide-footer>
      <basic-docket v-bind:docket="selectedDocket" v-bind:comments="podComments"></basic-docket>
      <b-form>
        <b-form-group label="New Comment">
          <b-form-textarea id="textarea1"
                           v-model="newComment"
                           placeholder="Enter a Comment"
                           :rows="3"
                           :max-rows="6">
          </b-form-textarea>
        </b-form-group>
      </b-form>
      <div class="row justify-content-end mt-5">
        <div class="col-auto">
          <b-btn size="" class="" variant="primary" @click="saveComment">
            <i class="fa fa-save"></i> Save Comment
          </b-btn>

          <b-btn size="" class="" variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>

    <b-modal id="bulkFileDownload" ref="bulkFileDownload" title="Bulk Download PODs" size="lg" centered lazy
             hide-footer>
      <b-form-group label="Select Excel File (XLS)">
        <b-form-file v-model="bulkPODFile" plain
                     @input="bulkPODFileSelected()"
                     ref="podFilesInput"
                     accept=".xls"
                     placeholder="Choose file ..."></b-form-file>
      </b-form-group>
      <b-alert variant="danger" show v-if="bulkPODError"><i class="fa fa-exclamation-circle"></i>
        {{ bulkPODError }}
      </b-alert>
      <div v-if="bulkPODUploading" class="row justify-content-center m-5">
        <loading></loading>
      </div>
      <table class="table mt-3 table-borderless" v-if="bulkPODList.length > 0">
        <thead>
        <tr>
          <th>
            Click Download Links One by One, in one batch 100 PODs will be available as zip File
          </th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(idx, index) in bulkPODList" :key="idx">
          <td>
            <a :href="`/inpod/misc/bulk-download?id=${idx}`" target="_blank" class="btn btn-outline-info">Download Part - {{index+1}} Zip File</a>
          </td>
        </tr>
        </tbody>
      </table>
      <div class="row justify-content-end mt-5">
        <div class="col-auto">

          <a class="btn btn-info" :href="samplePODDownloadExcelLink">
            <i class="fa fa-download"></i> Excel Template
          </a>
          <b-btn variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>
    <b-modal id="bulkDeliveryStatus" ref="bulkDeliveryStatus" title="Update Delivery Status in Bulk" size="lg" centered
             lazy hide-footer>

      <b-form>
        <b-alert variant="info" show><i class="fa fa-exclamation-circle"></i> Excel Template Has Changed, Kindly
          Download New Excel
        </b-alert>
        <b-alert variant="danger" show v-if="deliveryStatusError"><i class="fa fa-exclamation-circle"></i>
          {{ deliveryStatusError }}
        </b-alert>

        <b-form-group label="Select Excel File (XLS)">
          <b-form-file v-model="deliveryStatusFile" plain
                       @input="deliveryStatusFileSelected()"
                       ref="podFilesInput"
                       accept=".xls"
                       placeholder="Choose file ..."></b-form-file>
        </b-form-group>

        <div v-if="deliveryStatusUploading" class="row justify-content-center m-5">
          <loading></loading>
        </div>
        <table class="table mt-3" v-if="deliveryUpdatedList.length > 0">
          <thead class="thead-dark">
          <tr>
            <th>Document Type</th>
            <th>LR/Invoice No</th>
            <th>Delivery Date (YYYY-MM-DD)</th>
            <th>Shipment Status</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="ds of deliveryUpdatedList" :key="ds.lrNo">
            <td>{{ ds.docType }}</td>
            <td>{{ ds.lrNo }}</td>
            <td>{{ ds.delDt }}</td>
            <td>{{ ds.delSt }}</td>
            <td>
              <span v-if="!ds.ok" class="text-danger">{{ ds.err }}</span>
              <span v-if="ds.ok" class="text-success">Updated</span>
            </td>
          </tr>
          </tbody>
        </table>

      </b-form>
      <div class="row justify-content-end mt-5">
        <div class="col-auto">

          <a class="btn btn-info" :href="sampleDeliveryStatusExcelLink">
            <i class="fa fa-download"></i> Excel Template
          </a>
          <b-btn variant="warning" @click="hideModal">
            <i class="fa fa-times"></i> Close
          </b-btn>
        </div>
      </div>
    </b-modal>
  </section>
</template>
<script>
import axios from 'axios'
import moment from 'moment'
import Vue from 'vue'
import BasicDocket from '@/components/shared/BasicDocket'
import PODStatus from '@/components/shared/PODStatus'
import DeliveryStatus from '@/components/shared/DeliveryStatus'
import Loading from '@/components/shared/Loading'

export default {
  name: 'PODManage',
  components: {BasicDocket, PODStatus, Loading, DeliveryStatus},
  data () {
    return {
      saleTypes: ['STR', 'SAL', 'SAM'],
      goodPODOptions: [
        {text: 'Good & Bad POD', value: 'IGNORE'},
        {text: 'Only Good POD', value: 'GOOD'},
        {text: 'Only Bad POD', value: 'BAD'}
      ],
      podVerifiedStatus: [
        {text: 'Verified & Un-Verified', value: 'IGNORE'},
        {text: 'Only Verified POD', value: 'VERIFIED'},
        {text: 'Only Un-Verified POD', value: 'UN_VERIFIED'}
      ],
      cancelledOptions: [
        {text: 'Cancelled & Active', value: 'IGNORE'},
        {text: 'Only Cancelled Invoice', value: 'CANCELLED'},
        {text: 'Only Active Invoice', value: 'ACTIVE'}
      ],
      npdOptions: [
        {text: 'ALL', value: 'IGNORE'},
        {text: 'Only Service Invoices', value: 'INCLUDE'},
        {text: 'Only Non Service Invoices', value: 'EXCLUDE'}
      ],
      strOptions: [
        {text: 'ALL', value: 'IGNORE'},
        {text: 'Only STR', value: 'INCLUDE'},
        {text: 'Only Non STR', value: 'EXCLUDE'}
      ],
      noPODOptions: [
        {text: 'Pending & Uploaded POD', value: 'IGNORE'},
        {text: 'Only Pending POD', value: 'MISSING'},
        {text: 'Only Uploaded POD', value: 'PRESENT'}
      ],
      deliveredOptions: [
        {text: 'Delivered & Undelivered & Returned', value: 'IGNORE'},
        {text: 'Only Delivered', value: 'DELIVERED'},
        {text: 'Only Undelivered', value: 'IN_TRANSIT'},
        {text: 'Only Returned', value: 'RETURNED'}
      ],
      shipmentOptions: [
        {text: 'Shipped & Not-Shipped', value: 'IGNORE'},
        {text: 'Only Shipped', value: 'SHIPPED'},
        {text: 'Only Not Shipped', value: 'NOT_SHIPPED'}
      ],
      sort: 'desc',
      sortBy: 'docDt',
      savingBulkPOD: false,
      bulkValidated: false,
      sizeValidated: null,
      previewUrl: null,
      previewType: null,
      newComment: '',
      deliveryStatus: null,
      podComments: [],
      podFiles: [],
      deliveryUpdatedList: [],
      deliveryStatusFile: null,
      bulkPODFile: null,
      bulkPODUploading: false,
      deliveryStatusUploading: false,
      deliveryStatusError: null,
      bulkPODError: null,
      bulkPods: [],
      bulkPODList: [],
      goodPod: null,
      add: null,
      podFile: null,
      selectedDocket: null,
      currentPage: 1,
      totalItems: 0,
      perPage: 100,
      options: {
        format: 'YYYY-MM-DD',
        useCurrent: false,
        keepOpen: false,
        collapse: false,
        toolbarPlacement: 'bottom',
        showTodayButton: false,
        showClose: false,
        debug: false
      },
      inlineOptions: {
        format: 'YYYY-MM-DD',
        useCurrent: false,
        keepOpen: false,
        collapse: false,
        toolbarPlacement: 'bottom',
        showTodayButton: false,
        showClose: false,
        debug: false,
        inline: true
      },
      locations: [],
      transporters: [],
      customers: [],
      divisions: [],
      globalDivisions: [],
      s: {
        fromDate: moment().startOf('month').toDate(),
        toDate: moment().toDate(),
        docType: [],
        location: [],
        transporter: [],
        customer: []
      },
      f: {},
      filterObj: {
        hasFilter: false,
        invoiceNo: '',
        division: '',
        divisions: [],
        lrNo: '',
        ewaybillNo: '',
        noPOD: 'IGNORE',
        goodPOD: 'IGNORE',
        delivered: 'IGNORE',
        disputed: 'IGNORE',
        verified: 'IGNORE',
        shipped: 'IGNORE',
        npdShipment: 'EXCLUDE',
        strShipment: 'IGNORE',
        saleTypes: ['SAL', 'STR', 'SAM'],
        cancelled: 'IGNORE'
      },
      pod: [],
      filteredPod: [],
      fetchedDivisions: [],
      visibleDatePicker: null,
      docTypeOptions: [
        {value: 'INV', text: 'INVOICE'},
        {value: 'LR', text: 'LR No'}
      ],
      showSearchPanel: true,
      podSearching: false,
      filters: [],
      deleteSelected: ''
    }
  },
  computed: {
    availableOptions () {
      return this.saleTypes.filter(opt => this.f.saleTypes.indexOf(opt) === -1)
    },
    iframeHeight () {
      return 600
    },
    iframeWidth () {
      try {
        return document.querySelector("#podView .modal-dialog").offsetWidth * 0.9
      }catch (e) {
        console.error('error in getting width', e)
      }
      return window.innerWidth / 1.5
    },
    sFromDate () {
      return moment(this.s.fromDate).format('YYYY-MM-DD')
    },
    sToDate () {
      return moment(this.s.toDate).format('YYYY-MM-DD')
    },
    isTransporter () {
      return this.$store.state.userType === 'TRANSPORTER'
    },
    isCustomer () {
      return this.$store.state.userType === 'CONSIGNEE'
    },
    isUser () {
      return this.$store.state.userType === 'REGULAR_USER'
    },
    isAdmin () {
      return this.$store.state.adminLoggedIn
    },
    isLocalAdmin () {
      return this.$store.state.localAdmin
    },
    csvDownloadUrl () {
      let host = process.env.API_URL
      if (host.endsWith('/') === false) host = host + '/'
      return `${host}pod-app/pod-excel?csv=true&x-auth-token=${this.$store.state.accessToken}`
    },
    imageDownloadUrl () {
      let host = process.env.API_URL
      if (host.endsWith('/') === false) host = host + '/'
      return `${host}pod-app/pod-images?x-auth-token=${this.$store.state.accessToken}`
    },
    sampleDeliveryStatusExcelLink () {
      let host = process.env.API_URL
      if (host.endsWith('/') === false) host = host + '/'
      return `${host}pod-app/delivery-status-excel?x-auth-token=${this.$store.state.accessToken}`
    },
    samplePODDownloadExcelLink () {
      let host = process.env.API_URL
      if (host.endsWith('/') === false) host = host + '/'
      return `${host}pod-app/pod-download-excel?x-auth-token=${this.$store.state.accessToken}`
    },
    makeFilters () {
      console.log('filters', this.f)
      if (!this.f.hasFilter) return []
      let f = []
      if (this.f.invoiceNo.length > 0) {
        f.push({
          what: `Invoice`,
          val: this.f.invoiceNo
        })
      }
      if (this.f.lrNo.length > 0) {
        f.push({
          what: `LRNo`,
          val: this.f.lrNo
        })
      }
      if (this.f.divisions.length > 0) {
        f.push({
          what: `Divisions`,
          val: this.f.divisions.map(d => d.text).join(',')
        })
      }
      if (this.f.saleTypes.length > 0) {
        f.push({
          what: `Sale Types`,
          val: this.f.saleTypes.join(',')
        })
      }
      if (this.f.cancelled !== 'IGNORE') {
        if (this.f.cancelled === 'ACTIVE') {
          f.push({
            what: `Active Invoices`,
            val: 'Yes'
          })
        } else {
          f.push({
            what: `Cancelled Invoices`,
            val: 'Yes'
          })
        }
      }
      if (this.f.noPOD !== 'IGNORE') {
        if (this.f.noPOD === 'MISSING') {
          f.push({
            what: `POD Uploaded`,
            val: 'No'
          })
        } else {
          f.push({
            what: `POD Uploaded`,
            val: 'Yes'
          })
        }
      }
      if (this.f.goodPOD !== 'IGNORE') {
        if (this.f.goodPOD === 'GOOD') {
          f.push({
            what: `GoodPOD`,
            val: 'Yes'
          })
        } else {
          f.push({
            what: `BadPOD`,
            val: 'Yes'
          })
        }
      }
      if (this.f.delivered !== 'IGNORE') {
        if (this.f.delivered === 'DELIVERED') {
          f.push({
            what: `Shipment Delivered`,
            val: 'Yes'
          })
        } else if (this.f.delivered === 'IN_TRANSIT') {
          f.push({
            what: `Shipment Delivered`,
            val: 'No'
          })
        } else {
          f.push({
            what: `Shipment Returned`,
            val: 'Yes'
          })
        }
      }
      if (this.f.verified !== 'IGNORE') {
        if (this.f.verified === 'VERIFIED') {
          f.push({
            what: `POD Verified`,
            val: 'Yes'
          })
        }
        if (this.f.verified === 'UN_VERIFIED') {
          f.push({
            what: `POD Verified`,
            val: 'No'
          })
        }
      }
      if (this.f.npdShipment !== 'IGNORE') {
        if (this.f.npdShipment === 'INCLUDE') {
          f.push({
            what: `Service Invoices`,
            val: 'Yes'
          })
        }
        if (this.f.npdShipment === 'EXCLUDE') {
          f.push({
            what: `Service Invoices`,
            val: 'No'
          })
        }
      }
      if (this.f.strShipment !== 'IGNORE') {
        if (this.f.strShipment === 'INCLUDE') {
          f.push({
            what: `STR Invoices`,
            val: 'Yes'
          })
        }
        if (this.f.strShipment === 'EXCLUDE') {
          f.push({
            what: `STR Invoices`,
            val: 'No'
          })
        }
      }
      if (this.f.shipped !== 'IGNORE') {
        if (this.f.shipped === 'SHIPPED') {
          f.push({
            what: `Only Shipped`,
            val: 'Yes'
          })
        }
        if (this.f.shipped === 'NOT_SHIPPED') {
          f.push({
            what: `Only Not Shipped`,
            val: 'No'
          })
        }
      }
      console.log('filters', f)
      return f
    },
    downloadQueue () {
      return this.$store.state.downloadImages
    }
  },
  methods: {
    fx: () => false,
    showFilter () {
      this.$bvModal.show('filterEwbs')
    },
    fetchADD (f) {
      axios.get(`/pod-app/fetch-add?lrNo=${f.lrNo}&docType=${f.docType}`)
        .then(resp => {
          f.add = resp.data.add
          f.goodPod = resp.data.goodPod
          console.log('for LRNo', f.lrNo, 'ADD', resp.data)
        }, err => {
          console.error('error in fetching add', err)
          f.add = null
        })
    },
    setDatePickerVisible (idx) {
      this.visibleDatePicker = idx
    },
    showDatePicker (idx) {
      return this.visibleDatePicker === idx
    },
    sortPOD (colName) {
      this.sortBy = colName
      let sort = this.sort
      if (sort === 'asc') {
        this.sort = 'desc'
      } else {
        this.sort = 'asc'
      }
      this.changePage()
    },
    deletePOD () {
      let s = this.selectedDocket
      axios.post(`/pod-app/delete-pod?id=${this.selectedDocket.id}&delete=${this.deleteSelected}`)
        .then(_ => {
          this.pod.forEach((p, idx) => {
            if (p.id === s.id) {
              if (this.deleteSelected === 'pod') {
                p.containsPOD = false
                p.goodPod = null
              } else if (this.deleteSelected === 'add') {
                p.actualDeliveryDate = null
                p.deliveryStatus = 'IN_TRANSIT'
              } else if (this.deleteSelected === 'both') {
                p.containsPOD = false
                p.goodPod = null
                p.actualDeliveryDate = null
                p.deliveryStatus = 'IN_TRANSIT'
              }
              Vue.set(this.pod, idx, p)
            }
            this.hideModal()
          })
        }, err => {
          console.error('error in deleting', this.deleteSelected, err)
        })
    },
    sLocations () {
      return this.s.location.map(l => l.id).join(',')
    },
    sTransporters () {
      return this.s.transporter.map(l => l.id).join(',')
    },
    sCustomers () {
      return this.s.customer.map(l => l.id).join(',')
    },
    sFilter () {
      let f = Object.assign({}, this.f)
      f['divisions'] = f['divisions'].map(d => d.text)
      return JSON.stringify(f)
    },
    onFileChange (e) {
      const file = e.target.files[0]
      this.previewUrl = URL.createObjectURL(file)
      if (file.name.toLowerCase().endsWith('pdf')) {
        this.previewType = 'pdf'
      } else {
        this.previewType = 'image'
      }
    },
    downloadBulk () {
      setTimeout(() => {
        this.$store.commit('clearDownloadQueue')
      }, 1000)
    },
    removeFromDownloadQueue (p) {
      this.$store.commit('removeFromDownloadQueue', p.id)
    },
    addToDownloadQueue (p) {
      this.$store.commit('addToDownloadQueue', p.id)
    },
    toggleFilter () {
      this.f.hasFilter = !this.f.hasFilter
    },
    toggleSearchPanel () {
      this.showSearchPanel = !this.showSearchPanel
    },
    viewPodLink(pi, download) {
      let host = process.env.API_URL
      if (host.endsWith('/') === false) host = host + '/'
      return `${host}pod-app/pod-view?id=${pi.id}&x-auth-token=${this.$store.state.accessToken}&download=${download}`
    },
    verifiedPOD () {
      axios.post(`/pod-app/pod-status?id=${this.selectedDocket.id}&status=VERIFIED`, {
        comment: ''
      })
        .then(resp => {
          console.log('pos status update Okay', resp.data)
          this.pod.filter(p => p.id === this.selectedDocket.id).forEach(p => {
            p.disputeStatus = 'VERIFIED'
            p.goodPod = true
            p.comments = resp.data.comments
          })
          this.$refs.podView.hide()
        }, error => {
          console.error('error in updating poStatus', error)
        })
    },
    makeBadPOD () {
      axios.post(`/pod-app/pod-status?id=${this.selectedDocket.id}&status=RAISED`, {
        comment: ''
      })
        .then(resp => {
          console.log('pos status update Okay', resp.data)
          this.pod.filter(p => p.id === this.selectedDocket.id).forEach(p => {
            p.disputeStatus = 'RAISED'
            p.goodPod = false
          })
          this.$refs.podView.hide()
        }, error => {
          console.error('error in updating poStatus', error)
        })
    },
    savePOD () {
      if (this.podFile == null) return
      let fd = new FormData()
      let idx1 = this.podFile.name.indexOf(this.selectedDocket.docNo) === -1
      let idx2 = this.podFile.name.indexOf(this.selectedDocket.lrNo) === -1
      if (idx1 && idx2) {
        alert('POD File name Must Contain LR No or Invoice No')
        return
      }
      fd.append('file', this.podFile, this.podFile.name)
      fd.append('comment', this.newComment)
      fd.append('goodPod', this.goodPod)
      if (!this.add) {
        alert('Enter Delivery Date')
        return
      }
      if (!this.deliveryStatus) {
        alert('Enter Delivery Status')
        return
      }
      let add = moment(this.add).format('YYYY-MM-DD')
      let addM = moment(this.add).startOf('day')
      let invDt = moment(this.selectedDocket.docDt).startOf('day')
      if (addM.isBefore(invDt)) {
        alert('Delivery Date Cannot be Before Invoice Date')
        return
      }
      if (addM.isAfter(moment())) {
        alert('Delivery Date Cannot be in future')
        return
      }
      if (this.selectedDocket.lrDt) {
        let lrDt = moment(this.selectedDocket.lrDt).startOf('day')
        if (addM.isBefore(lrDt)) {
          alert('Delivery Date Cannot be Before LR Date')
          return
        }
      }

      axios.post(`/pod-app/file?document_id=${this.selectedDocket.id}&comment=${this.newComment}&goodPod=${this.goodPod}&add=${add}&deliveryStatus=${this.deliveryStatus}`, fd)
        .then(resp => {
          this.filteredPod.forEach((p, idx) => {
            if (p.id === this.selectedDocket.id) {
              p.containsPOD = true
              p.goodPod = this.goodPod
              p.actualDeliveryDate = this.add
              p.deliveryStatus = this.deliveryStatus
              p.comments = resp.data.comments
              Vue.set(this.filteredPod, idx, p)
            }
          })

          this.add = null
          this.podFile = null
          this.goodPod = null
          this.newComment = ''
          this.deliveryStatus = null
          this.loadCommentsAndPOD()
          this.$refs.podUpload.hide()
        }, error => {
          console.error('error in file upload', error)
        })
    },
    deliveryStatusFileSelected () {
      if (this.deliveryStatusFile) {
        this.deliveryStatusUploading = true

        let fd = new FormData()
        fd.append('file', this.deliveryStatusFile, 'file')

        axios.post(`/pod-app/delivery-status`, fd)
          .then(resp => {
            this.deliveryStatusUploading = false
            this.deliveryUpdatedList = resp.data
            this.deliveryStatusError = null
            this.changePage()
          }, err => {
            this.deliveryStatusUploading = false
            this.deliveryStatusError = 'Error in Uploading Excel File'
            console.error('error in update of delivery status', err)
          })
      }
    },
    bulkPODFileSelected () {
      if (this.bulkPODFile) {
        this.bulkPODUploading = true

        let fd = new FormData()
        fd.append('file', this.bulkPODFile, 'file')

        axios.post(`/misc/bulk-download`, fd)
          .then(resp => {
            this.bulkPODUploading = false
            this.bulkPODList = resp.data
            this.bulkPODError = null
          }, err => {
            this.bulkPODList = []
            this.bulkPODUploading = false
            this.bulkPODError = 'Error in Uploading Excel File'
            console.error('error in update of delivery status', err)
          })
      }
    },
    filesSelected(evt) {
      setTimeout(() => {
        this.bulkPods = []
        let errCount = 0
        this.podFiles.forEach((file, idx) => {
          let errMsg = null
          let status = null
          if (file.size < 10) {
            errMsg = 'Invalid File Size'
            status = 'danger'
            errCount += 1
          }
          let f = {
            idx: idx,
            file: file,
            lrNo: file.name.split('.')[0],
            name: file.name,
            docType: 'INV',
            comment: '',
            goodPod: null,
            add: null,
            deliveryStatus: 'DELIVERED',
            errMsg: errMsg,
            status: status
          }
          this.bulkPods.push(f)
          this.fetchADD(f)
        })
        this.sizeValidated = errCount === 0
      }, 1000)
    },
    savePODs () {
      if (this.bulkPods.length === 0) return
      this.savingBulkPOD = true
      let totalToUpload = this.bulkPods.length
      let uploaded = 0
      let checkAndHideModal = () => {
        uploaded++

        if (uploaded === totalToUpload) {
          setTimeout(() => {
            this.savingBulkPOD = false
            this.sizeValidated = null
            this.changePage()
            this.hideModal()
          }, 3000)
        } else {
          console.log('keep modal open', uploaded, totalToUpload)
        }
      }
      this.bulkPods.forEach(pod => {
        pod.status = 'info'
        pod.errMsg = 'File Uploading....'
      })
      this.bulkPods.forEach(pod => {
        let fd = new FormData()
        let add = moment(pod.add).format('YYYY-MM-DD')
        fd.append('file', pod.file, pod.name)
        axios
          .post(`/pod-app/files?lrNo=${pod.lrNo}&goodPod=${pod.goodPod}&comments=${pod.comment}&add=${add}&deliveryStatus=${pod.deliveryStatus}&docType=${pod.docType}`, fd)
          .then(resp => {
            pod.status = 'success'
            pod.errMsg = 'File Uploaded'
            console.log('File Upload OK', resp)
            checkAndHideModal()
          }, err => {
            pod.status = 'danger'
            pod.errMsg = 'File Upload Failed'
            checkAndHideModal()
            console.error('error in file Upload', err)
          })
      })
    },
    hideModal () {
      try {
        if (this.$refs.podView) this.$refs.podView.hide()
        if (this.$refs.podUpload) this.$refs.podUpload.hide()
        if (this.$refs.bulkPodUpload) this.$refs.bulkPodUpload.hide()
        if (this.$refs.commentView) this.$refs.commentView.hide()
        if (this.$refs.bulkDeliveryStatus) this.$refs.bulkDeliveryStatus.hide()
        if (this.$refs.podFilesInput) this.$refs.podFilesInput.reset()
        if (this.$refs.podDelete) this.$refs.podDelete.reset()
        if (this.$refs.bulkFileDownload) this.$refs.bulkFileDownload.reset()
      } catch (e) {
        console.error('error in hiding modals', e)
      }
      this.bulkPods = []
      this.podFiles = []
      this.deliveryUpdatedList = []
      this.bulkValidated = false
      this.previewUrl = false
    },
    saveComment () {
      axios.post(`/pod-app/comment?document_id=${this.selectedDocket.id}`, {comment: this.newComment})
        .then(resp => {
          this.pod.filter(p => p.id === this.selectedDocket.id).forEach(p => {
            p.comments.push(this.newComment)
          })
          this.newComment = ''
          this.loadCommentsAndPOD()
        }, error => {
          console.error('error in saving comment', error)
        })
    },
    loadCommentsAndPOD () {
      axios.all([
        axios.get(`/pod-app/comment?document_id=${this.selectedDocket.id}`),
        axios.get(`/pod-app/file?document_id=${this.selectedDocket.id}`)
      ]).then(axios.spread((comments, files) => {
        this.podComments = comments.data
        this.podFiles = files.data
      }), err => {
        console.error('error in fetching comments/files', err)
      })
    },
    showPOD (f) {
      this.selectedDocket = f
      this.newComment = ''
      this.loadCommentsAndPOD()
      this.$refs.podView.show()
    },
    uploadPOD (f) {
      this.selectedDocket = f
      console.log(f.actualDeliveryDate, this.add)
      if (f.actualDeliveryDate) {
        this.add = new Date(f.actualDeliveryDate)
      }
      this.loadCommentsAndPOD()
    },

    podDelete (f) {
      this.selectedDocket = f
      this.loadCommentsAndPOD()
    },
    addComments (f) {
      this.selectedDocket = f
      this.newComment = ''
      this.loadCommentsAndPOD()
    },
    raiseDispute (f) {
      this.selectedDocket = f
      this.loadCommentsAndPOD()
    },
    clear () {
      this.f = Object.assign({}, this.filterObj)
      this.$bvModal.hide('filterEwbs')
      this.pod = []
      this.$store.commit('clearDownloadQueue')
      this.$router.push('/manage-pod')
    },
    getPOD () {
      let mf = moment(this.s.fromDate)
      let mt = moment(this.s.toDate)
      let diff = mf.diff(mt, 'months')
      if (Math.abs(diff) > 2) {
        alert('search range cannot be more than 3 months')
        return
      }
      if (mf.isAfter(mt)) {
        alert('From Date cannot be after To Date')
        return
      }
      this.currentPage = 1
      this.changePage()
      this.$store.commit('clearDownloadQueue')
    },
    changePage () {
      let s = Object.assign({}, this.s)
      s['location'] = s['location'].map(l => {
        return l.id
      })
      s['transporter'] = s['transporter'].map(l => {
        return l.id
      })
      s['customer'] = s['customer'].map(l => {
        return l.id
      })
      s['currentPage'] = this.currentPage
      s['perPage'] = this.perPage
      s['filter'] = Object.assign({}, this.f)
      s['filter']['divisions'] = this.f.divisions.map(d => d.text)
      s['sort'] = this.sort
      s['sortBy'] = this.sortBy
      s['fromDate'] = moment(this.s.fromDate).format('YYYY-MM-DD')
      s['toDate'] = moment(this.s.toDate).format('YYYY-MM-DD')
      this.podSearching = true
      axios.post('/pod-app/pod', s)
        .then(resp => {
          this.totalItems = resp.data.totalItems
          this.pod = resp.data.paginated
          this.filteredPod = this.pod
          if (this.currentPage === 1) {
            this.divisions = resp.data['divisions'].map(d => {
              return {
                label: d,
                text: d
              }
            })
          }
          this.podSearching = false
        }, error => {
          this.podSearching = false
          console.error('error in loading pods', error)
        })
    },
    filterPOD () {
      let s = Object.assign({}, this.s)
      this.currentPage = 1
      s['location'] = s['location'].map(l => {
        return l.id
      })
      s['transporter'] = s['transporter'].map(l => {
        return l.id
      })
      s['customer'] = s['customer'].map(l => {
        return l.id
      })
      s['currentPage'] = this.currentPage
      s['perPage'] = this.perPage
      this.f.hasFilter = true
      s['filter'] = Object.assign({}, this.f)
      s['filter']['divisions'] = this.f.divisions.map(d => d.text)
      s['fromDate'] = moment(this.s.fromDate).format('YYYY-MM-DD')
      s['toDate'] = moment(this.s.toDate).format('YYYY-MM-DD')

      axios.post('/pod-app/pod', s)
        .then(resp => {
          this.totalItems = resp.data.totalItems
          this.pod = resp.data.paginated
          this.filteredPod = this.pod
          this.$bvModal.hide('filterEwbs')
        }, error => {
          console.error('error in loading pods', error)
        })
    },
    filter () {
      this.filteredPod = this.pod

      this.currentPage = 1
    },
    loadDashboard () {
      if (this.$route.params['what']) {
        this.f = Object.assign({}, this.filterObj)

        this.s.fromDate = moment(this.$route.params.from)
        this.s.toDate = moment(this.$route.params.to)
        let group = this.$route.params['group'] || '0'
        let division = (this.$route.params['division'] || '0').replace('_', ' ')
        if (this.$route.params.loc !== '0') {
          let loc = parseInt(this.$route.params.loc)
          this.locations.filter(l => l.id === loc)
            .forEach(l => {
              this.s.location.push(l)
            })
        } else {
          console.log('skipping location')
        }
        let urlDivisions = []
        if (group !== '0') {
          if (division === '0') {
            urlDivisions = this.$store.state.divisions.filter(d => d.group === group).map(d => d.division)
          } else {
            urlDivisions = [division]
          }
          this.f.divisions = urlDivisions.map(d => {
            return {
              text: d,
              value: d
            }
          })
          this.f.hasFilter = urlDivisions.length > 0
          console.log('divisions', group, division, urlDivisions)
        } else {
          console.log('ignore', group)
        }
        if (this.$route.params.tr !== '0') {
          let tr = parseInt(this.$route.params.tr)
          this.transporters.filter(l => l.id === tr)
            .forEach(l => {
              this.s.transporter.push(l)
            })
        } else {
          console.log('skipping transporter')
        }

        if (this.$route.params.what === 'created') {
          console.log('created, do nothing')
        } else if (this.$route.params.what === 'delivered') {
          console.log('delivered')
          this.f.delivered = 'DELIVERED'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'returned') {
          console.log('returned')
          this.f.delivered = 'RETURNED'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'uploaded') {
          console.log('uploaded')
          this.f.noPOD = 'PRESENT'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'shipped') {
          console.log('shipped')
          this.f.cancelled = 'ACTIVE'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'cancelled') {
          console.log('cancelled')
          this.f.cancelled = 'CANCELLED'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'services') {
          console.log('no shipping')
          this.f.npdShipment = 'INCLUDE'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'pending') {
          console.log('pending')
          this.f.noPOD = 'MISSING'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'disputed') {
          console.log('disputed')
          this.f.goodPOD = 'BAD'
          this.f.hasFilter = true
        } else if (this.$route.params.what === 'undelivered') {
          console.log('undelivered')
          this.f.delivered = 'IN_TRANSIT'
          this.f.hasFilter = true
        }

        if (this.$route.params.saletypes) {
          this.f.hasFilter = true
          this.f.saleTypes = this.$route.params.saletypes.split(',')
        }
        let s = Object.assign({}, this.s)
        s['location'] = s['location'].map(l => {
          return l.id
        })
        s['transporter'] = s['transporter'].map(l => {
          return l.id
        })
        s['customer'] = s['customer'].map(l => {
          return l.id
        })
        s['currentPage'] = this.currentPage
        s['perPage'] = this.perPage
        s['filter'] = Object.assign({}, this.f)
        s['filter']['divisions'] = this.f.divisions.map(d => d.text)
        s['fromDate'] = moment(this.s.fromDate).format('YYYY-MM-DD')
        s['toDate'] = moment(this.s.toDate).format('YYYY-MM-DD')
        axios.post('/pod-app/pod', s)
          .then(resp => {
            this.totalItems = resp.data.totalItems
            this.pod = resp.data.paginated
            this.divisions = resp.data.divisions.map(d => {
              return {value: d, text: d}
            })
            this.filter()
          }, error => {
            console.error('error in loading pods', error)
          })
      } else {
        console.log('reports page')
      }
    },
    validatePods () {
      let allReq = this.bulkPods.map(pod => {
        return {
          'lrNo': pod.lrNo,
          'docType': pod.docType,
          'add': moment(pod.add).format('YYYY-MM-DD')
        }
      })
      axios.post('/pod-app/validate-add', allReq)
        .then(resp => {
          resp.data.forEach(p => {
            let err = 0
            this.bulkPods.forEach((bp, idx) => {
              if (bp.lrNo === p.lrNo) {
                bp.errMsg = p.errMsg
                bp.status = p.status
                bp.idx = idx
                if (p.status === 'danger') {
                  err += 1
                }
                Vue.set(this.bulkPods, idx, bp)
              }
            })
            this.bulkValidated = err === 0
          })
        }, err => {
          console.error('error in validate', err)
        })
    }
  },
  created () {
    // load required
    this.f = Object.assign({}, this.filterObj)
    this.$store.dispatch('isLoggedIn').then(() => {
      if (this.$store.state.loggedIn) {
        this.transporters = this.$store.state.transporters.map(t => {
          return {
            id: t.id,
            name: t.name.substring(0, 15)
          }
        })
        this.locations = this.$store.state.locations.filter(l => l.enabled !== false).map(t => {
          return {
            id: t.id,
            name: t.name.substring(0, 15),
            codes: t.codes
          }
        })
        this.customers = this.$store.state.customers.map(c => {
          return {
            id: c.id,
            name: c.name.substring(0, 20),
            code: c.code
          }
        })
        this.divisions = this.$store.state.divisions.map(q => {
          return {value: q, text: q}
        })
        this.globalDivisions = this.$store.state.divisions.map(q => q.group)
        this.loadDashboard()
      } else {
        this.$router.push('/login')
      }
    }, _ => {
      console.error('isLoggedIn Failed, lets go to login')
      this.$router.push('login')
    })
  }
}
</script>
<style scoped>
.vue-input-tag-wrapper.read-only {
  border: 0 none;
}

table.table-sm, table.table-sm > thead {
  font-size: 0.6rem;
}

.hide-excess {
  width: 100px;
  overflow: hidden;
}

.one-line-text {
  white-space: nowrap;
}

a {
  cursor: pointer;
}

.pod_image:hover img {
  -webkit-transform: scale(2); /* Safari and Chrome */
  -moz-transform: scale(2); /* Firefox */
  -ms-transform: scale(2); /* IE 9 */
  -o-transform: scale(2); /* Opera */
  transform: scale(2);
  position: relative;
  top: 10%;
  left: 10%;
  z-index: 19999;
}

.dp-holder {
  position: absolute;
  background: #ffffff;
  padding: 10px;
  border-right: 2px solid #CCCCCC;
  border-left: 2px solid #CCCCCC;
  border-bottom: 2px solid #CCCCCC;
  z-index: 9999;
}

#searchEwbs .form-control,
#filterEwbs .form-control {
  height: 36px;
}

th {
  z-index: 99;
}

thead.one th {
  position: sticky;
  top: 0;
}

thead.two th {
  position: sticky;
  top: 22px;
}
</style>



// WEBPACK FOOTER //
// src/components/PODManage.vue